import { Product } from "@omni/types/api/product/bff/product.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { StickyActionElement } from "@omni/types/components/general/enums/stickyActionElement.enum";
import { ProductSliderProps } from "@omni/types/components/props/productSlider.props";
import { Breakpoint } from "@omni/types/enums/breakPoint.enum";
import * as SessionStorage from "@omni/utils/sessionStorage.utils";
import { cn } from "@omni/utils/styles";
import { useEffect } from "react";

import { StoreAddress } from "@/components/Address/Address/address.component";
import PriceSummary from "@/components/Cart/ShoppingCart/priceSummary/priceSummary.component";
import ProductCollectionBlock from "@/components/Cart/ShoppingCart/productCollectionBlock/productCollectionBlock.component";
import ProductInfoBlock from "@/components/Cart/ShoppingCart/productInfoBlock/productInfoBlock.component";
import { PromotionItems } from "@/components/Cart/ShoppingCart/promotionItems/promotionItems.component";
import { CheckoutButton } from "@/components/Checkout/CheckoutButton/checkoutButton.container";
import { Discount } from "@/components/Discount/discount.component";
import { Row } from "@/components/General/Grid/row.component";
import { KlarnaOsm } from "@/components/Payment/KlarnaOsm/klarnaOsm";
import { ProductSliderDefault } from "@/components/Product/ProductSlider/productSlider.default";
import { StickyAction } from "@/components/StickyAction/stickyAction";
import {
    CartItemModel,
    CartModel
} from "@/redux/modules/api/data/cart/@types/cart.model";
import { AddToWishlistStatus } from "@/redux/modules/api/data/wishlist/@types/addToWishlistStatus.enum";
import { broadcastMessage } from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import {
    useIsBreakpoint,
    withBreakpoints
} from "@/utils/breakpoints/breakpoints.provider";
import { StorageKeys } from "@/utils/enums";

export type ShoppingCartProps = {
    removeFromCart: (
        id: string,
        quantity: number,
        isFreeAddedByCustomer: boolean
    ) => Promise<any>;
    updateCartItem: (
        id: string,
        quantity: number,
        deliveryType: string,
        product?: CartItemModel,
        quantityDelta?: number
    ) => Promise<any>;
    updateCartItemConfig: (
        id: string,
        config: Record<string, unknown>,
        product: CartItemModel,
        productSubType: string
    ) => Promise<any>;
    onAddAdditionalService: (
        cartItemId: string,
        serviceSku: string,
        product: CartItemModel
    ) => Promise<any>;
    onAddAccessory: (
        cartItemId: string,
        sku: string,
        product: Product<ProductFieldSet.ProductTile>
    ) => Promise<any>;
    onRemoveAdditionalService: (cartItemId: string) => Promise<any>;
    onChangeStore: (sku: string) => void;
    addToWishlist: (sku: string) => Promise<AddToWishlistStatus>;
    removeFromWishlist: (sku: string) => Promise<any>;
    shoppingCart: CartModel;
    loadingPromotionCode: boolean;
    loadingStore: boolean;
    storeDetails: CloudStore | null | undefined;
    addPromotionCode: (code: string, bonusCardNumber?: string) => void;
    removePromotionCode: (code: string) => void;
    changeStore: (store: string) => Promise<any>;
    ccTooltipInfo: string;
    dsvTooltipInfo: string;
    warrantyDepositTooltipInfo: string;
    cashbackTooltipInfo: string;
    customerFavoriteStore?: string | undefined;
    productSliderConfig: ProductSliderProps | null;
};

const _ShoppingCart = ({
    onChangeStore,
    removeFromCart,
    updateCartItem,
    onAddAdditionalService,
    onRemoveAdditionalService,
    onAddAccessory,
    storeDetails,
    shoppingCart,
    changeStore,
    ccTooltipInfo,
    dsvTooltipInfo,
    addPromotionCode,
    removePromotionCode,
    warrantyDepositTooltipInfo,
    cashbackTooltipInfo,
    customerFavoriteStore,
    loadingPromotionCode,
    loadingStore,
    updateCartItemConfig,
    addToWishlist,
    removeFromWishlist,
    productSliderConfig
}: ShoppingCartProps) => {
    const { t } = useGranite();

    const { matches: isMd, ready } = useIsBreakpoint("md");

    useEffect(() => {
        const cartsMerged = SessionStorage.getItem(StorageKeys.CartsMerged);

        if (!SessionStorage.getItem(StorageKeys.PreviousCartData)) {
            SessionStorage.setItem(
                StorageKeys.PreviousCartData,
                JSON.stringify([])
            );
        }

        if (cartsMerged && cartsMerged === "true") {
            broadcastMessage({
                id: "carts-merged",
                text: t("account.cartsMerged.success"),
                status: "success",
                dismissible: true
            });
        }
    }, [t]);

    const collectCartItems =
        shoppingCart?.items?.filter(
            (i) =>
                i.deliveryType === DeliveryType.ClickAndCollect &&
                !i.isAdditionalService
        ) || [];

    const deliveryCartItems =
        shoppingCart?.items?.filter(
            (i) =>
                i.deliveryType === DeliveryType.HomeDelivery &&
                !i.isAdditionalService
        ) || [];

    return (
        <>
            <ProductCollectionBlock
                icon={"la-hand-pointer"}
                title={t("general.cart.label.clickandcollect")}
                infoBlock={
                    <ProductInfoBlock
                        storeCode={storeDetails?.code}
                        storeName={
                            collectCartItems.length > 0 &&
                            !loadingStore &&
                            storeDetails !== null
                                ? t(
                                      "general.cart.label.currentStore",
                                      t(
                                          "general.shopName",
                                          storeDetails?.shopName
                                      )
                                  )
                                : undefined
                        }
                        onChangeStore={() => {
                            const sku = collectCartItems[0]?.sku;
                            if (!sku) return;

                            onChangeStore(sku);
                        }}
                        changeStore={changeStore}
                    />
                }
                cartItems={collectCartItems}
                deliveryType={DeliveryType.ClickAndCollect}
                removeFromCart={removeFromCart}
                updateCartItem={updateCartItem}
                updateCartItemConfig={updateCartItemConfig}
                addToWishlist={addToWishlist}
                removeFromWishlist={removeFromWishlist}
                ccTooltipInfo={ccTooltipInfo}
                dsvTooltipInfo={dsvTooltipInfo}
                warrantyDepositTooltipInfo={warrantyDepositTooltipInfo}
                cashbackTooltipInfo={cashbackTooltipInfo}
                addAdditionalService={onAddAdditionalService}
                removeAdditionalService={onRemoveAdditionalService}
                addAccessory={onAddAccessory}
            />
            <ProductCollectionBlock
                icon={"la-truck"}
                title={t("general.cart.label.delivery")}
                cartItems={deliveryCartItems}
                deliveryType={DeliveryType.HomeDelivery}
                removeFromCart={removeFromCart}
                updateCartItem={updateCartItem}
                updateCartItemConfig={updateCartItemConfig}
                addToWishlist={addToWishlist}
                removeFromWishlist={removeFromWishlist}
                changeStore={changeStore}
                onChangeStore={onChangeStore}
                ccTooltipInfo={ccTooltipInfo}
                dsvTooltipInfo={dsvTooltipInfo}
                warrantyDepositTooltipInfo={warrantyDepositTooltipInfo}
                cashbackTooltipInfo={cashbackTooltipInfo}
                preferredStore={customerFavoriteStore}
                addAdditionalService={onAddAdditionalService}
                removeAdditionalService={onRemoveAdditionalService}
                addAccessory={onAddAccessory}
            />
            <Row className="mt-3">
                {productSliderConfig && (
                    <ProductSliderDefault
                        className={"col-6"}
                        {...productSliderConfig}
                    />
                )}
                <div className="col-12 col-md-8 col-lg-3 mt-2 mt-md-0 order-1">
                    <Row>
                        <div className={"col-12 col-md-8 col-lg-12"}>
                            <PromotionItems
                                promotions={shoppingCart.promotions}
                            />
                            <Discount
                                cartHasItems={shoppingCart.items.length > 0}
                                isLoading={loadingPromotionCode}
                                promotionCodes={shoppingCart.promotionCodes}
                                addPromotionCode={addPromotionCode}
                                removePromotionCode={removePromotionCode}
                            />
                        </div>
                        <div className={"col-12 col-md-4 col-lg-12 mt-lg-4"}>
                            {collectCartItems.length > 0 && storeDetails && (
                                <div className="mt-4 mt-md-0">
                                    <StoreAddress
                                        address={storeDetails.address}
                                        storeName={storeDetails.shopName}
                                    />
                                </div>
                            )}
                        </div>
                    </Row>
                </div>
                <div className="col-12 col-md-4 col-lg-3 order-0 order-md-3">
                    <PriceSummary cart={shoppingCart} />
                    <div
                        className={cn(
                            "tw:hidden tw:my-4 tw:w-full tw:flex-col",
                            "tw:md:flex"
                        )}
                    >
                        <CheckoutButton fullWidth={true} />
                        {ready && isMd && (
                            <KlarnaOsm amount={shoppingCart.totalPrice} />
                        )}
                    </div>
                </div>
                <div
                    className={cn(
                        "tw:block tw:w-full tw:mt-4 tw:order-4",
                        "tw:md:hidden"
                    )}
                >
                    <CheckoutButton fullWidth={true} />
                    {ready && !isMd && (
                        <KlarnaOsm amount={shoppingCart.totalPrice} />
                    )}
                </div>
                {shoppingCart.items.length && (
                    <StickyAction
                        stickyActionElement={StickyActionElement.CheckoutButton}
                        hiddenBreakpoint={Breakpoint.md}
                    >
                        <CheckoutButton
                            fullWidth={true}
                            securePaymentInfo={false}
                        />
                    </StickyAction>
                )}
            </Row>
        </>
    );
};

export const ShoppingCartComponent = withBreakpoints(_ShoppingCart);
